import { Component, HostListener, Input, OnInit, output } from '@angular/core';
import {
	ApplicationContext,
	AuthnService,
	ComponentBase,
	DeploymentContext,
	MenuOption,
	SearchService,
	UserService,
	WebAnalyticsService,
} from '@Common';
import {
	Asset,
	AssetKey,
	hasRequiredChanges,
	IsArrayProperty,
	IUser,
	LocalizedTextIds,
	SortDetails,
	SortType,
	urlIdentifierForCompany,
} from 'company-finder-common';
import { ActivatedRoute, Router } from '@angular/router';
import {
	CompanyUpdateService,
	ReviewableUpdate,
	SelfUpdateMode,
} from '../../../app';

@Component({
	selector: 'asset-table',
	templateUrl: './asset-table.component.html',
	styleUrls: ['./asset-table.component.scss'],
	standalone: false,
})
export class AssetTableComponent extends ComponentBase implements OnInit {
	public constructor(
		private authnService: AuthnService,
		private router: Router,
		private webAnalyticsService: WebAnalyticsService,
		private userService: UserService,
		private appContext: ApplicationContext,
		private searchService: SearchService,
		private route: ActivatedRoute,
		public updateService: CompanyUpdateService,
		dc: DeploymentContext
	) {
		super(dc);
	}

	public user: IUser;
	public columnOptions: MenuOption<string>[];
	public SelfUpdateModes = SelfUpdateMode;

	public onSortChanged = output<SortDetails>();

	@HostListener('window:click')
	protected onClick(): void {
		this.showMenu = false;
	}

	public ngOnInit(): void {
		this.user = this.route.snapshot.data.user;
		// When loading the table, start with the user-provided
		// columns on screen
		this.visibleColumns = this.initialColumns;
		// If a full column list wasn't provided,
		// use the list of initial columns as the list
		if (this.availableColumns.length === 0) {
			this.availableColumns = this.initialColumns;
		}
		this.columnOptions = this.availableColumns.map((option) => ({
			id: option,
			label: this._deploymentContext.getHeaderForAssetProperty(option),
			value: !!this.visibleColumns?.find(
				(filterOption) => filterOption === option
			),
			dataModel: option,
		}));
	}

	@Input()
	public enableFollowCheckbox = true;

	@Input()
	public enableCompanyNav: boolean = false;

	@Input()
	public enableAssetNav: boolean = true;

	@Input()
	public enableCustomRowAction: boolean = false;

	@Input()
	public embeddedScroll: boolean = false;

	@Input()
	public disableSort: boolean = false;

	@Input()
	public fullWitdh: boolean = false;

	@Input()
	public enableDownload: boolean = false;

	@Input()
	public customRowAction?: (asset: Asset) => unknown;

	@Input()
	public assets: Asset[] = [];

	@Input()
	public initialColumns: AssetKey[] = [
		'name',
		'companyName',
		'sector',
		'focusArea',
		'therapeuticArea',
		'indication',
		'target',
		'routeOfAdministration',
		'developmentStage',
	];

	@Input()
	public availableColumns: AssetKey[] = [];

	public visibleColumns: AssetKey[] = [];

	@Input()
	public enableColumnSelection: boolean = false;

	@Input()
	public updateMode?: SelfUpdateMode = null;

	public showMenu = false;
	private sortColumn: AssetKey = 'name';
	private direction: 'asc' | 'desc' = 'asc';

	public areAllFollowed(): boolean {
		return !this.assets?.some(
			(asset) =>
				!this.user?.assetsFollowed?.some(
					(followed) => followed.assetId === asset.assetId
				)
		);
	}

	public get updateForReview(): ReviewableUpdate {
		return this.updateService.updateForReview;
	}

	public set updateForReview(value: ReviewableUpdate) {
		this.updateService.updateForReview = value;
	}

	public isFollowingAsset(asset) {
		return (
			this.user?.assetsFollowed?.some(
				(a) => a.assetId === asset.assetId
			) ?? false
		);
	}

	public async toggleFollowAll() {
		this.user = await this.userService.setFollowingAssets(
			this.assets.map((asset) => asset.assetId),
			!this.areAllFollowed()
		);
	}

	public async setFollowingAsset(assetId: string, valueToSet: boolean) {
		this.user = await this.userService.setFollowingAsset(
			assetId,
			valueToSet
		);
	}

	public toggleFollowingAsset(asset: Asset) {
		this.setFollowingAsset(asset.assetId, !this.isFollowingAsset(asset));
	}

	public get showCheckbox(): boolean {
		return this.enableFollowCheckbox && this.authnService.isInternal;
	}

	public get tableClass(): string {
		return `${this.embeddedScroll ? 'scrollable-table' : ''} ${
			this.fullWitdh ? 'full-width-table' : ''
		}`;
	}

	public get controlClass(): string {
		return `controls ${this.fullWitdh ? 'full-width' : ''}`;
	}

	public async exportAsCSV(): Promise<void> {
		this.showMenu = false;
		const assetsAsCsv = await this.searchService.searchAssetsForCsv(
			this.visibleColumns
		);
		this.appContext.saveDocument(
			this.Loc(LocalizedTextIds.Assets),
			assetsAsCsv,
			'csv'
		);
	}
	public async exportAsPDF(): Promise<void> {
		this.showMenu = false;
		const assetsAsCsv = await this.searchService.searchAssetsForPdf(
			this.visibleColumns
		);
		this.appContext.saveDocument(
			this.Loc(LocalizedTextIds.Assets),
			assetsAsCsv,
			'pdf'
		);
	}
	public setShowMenu(ev: MouseEvent): void {
		ev.preventDefault();
		ev.stopPropagation();

		this.showMenu = true;
	}

	public sortBy(sortKey: AssetKey) {
		if (sortKey === this.sortColumn) {
			this.direction = this.direction === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortColumn = sortKey;
			this.direction = 'asc';
		}

		this.onSortChanged.emit({
			field: this.sortColumn,
			direction: this.direction,
			type: SortType.Alpha,
		});

		if (!this.disableSort) {
			Asset.SortByProperty(this.assets, sortKey, this.direction);
		}
	}

	public getHeaderText(prop: AssetKey): string {
		return this._deploymentContext.getHeaderForAssetProperty(prop);
	}

	public selectValue(asset: Asset, prop: AssetKey): void {
		if (this.enableCustomRowAction) {
			return;
		}
		switch (prop) {
			case 'companyName':
				if (this.enableCompanyNav) {
					this.navigateToCompany(asset.companyName, asset.name);
				}
				break;
			case 'name':
				if (this.enableAssetNav) {
					this.navigateToAsset(asset.name);
				}
				break;
			default:
				return;
		}
	}

	public navigateToCompany(companyName: string, assetName: string): void {
		this.router.navigate(['company', urlIdentifierForCompany(companyName)]);
		this.webAnalyticsService.trackEvent('select-company-from-asset-table', {
			category: 'Company Details',
			label: `${companyName} from ${assetName}`,
		});
	}

	public navigateToAsset(assetName: string): void {
		this.router.navigate(['asset', urlIdentifierForCompany(assetName)]);
		this.webAnalyticsService.trackEvent('select-asset-from-search', {
			category: 'Company Details',
			label: `${assetName}`,
		});
	}

	private get clickbleStyle(): Partial<CSSStyleDeclaration> {
		return {
			cursor: 'pointer',
		};
	}

	public getStylesForRow(): Partial<CSSStyleDeclaration> {
		if (this.enableCustomRowAction) {
			return this.clickbleStyle;
		}
	}

	public rowClicked(asset: Asset) {
		if (!this.enableCustomRowAction) {
			return;
		}

		this.customRowAction(asset);
	}

	public selectedColumn(columns) {
		this.visibleColumns = columns;
	}

	public getStylesForProperty(prop: AssetKey): Partial<CSSStyleDeclaration> {
		switch (prop) {
			case 'companyName':
				if (this.enableCompanyNav) {
					return this.clickbleStyle;
				}
				break;
			case 'name':
				if (this.enableAssetNav) {
					return this.clickbleStyle;
				}
				break;
			default:
				return;
		}
	}

	public get downloadPDF(): string {
		return this.Loc(LocalizedTextIds.DownloadPDF);
	}

	public get downloadCSV(): string {
		return this.Loc(LocalizedTextIds.DownloadCSV);
	}

	public get download(): string {
		return this.Loc(LocalizedTextIds.Download);
	}

	public getValue(asset: Asset, column: AssetKey) {
		const val = asset[column];

		// FUTURE - it was discovered 12/16/25 that a company with
		// an approved change to the therapeutic area of an associated
		// asset can result in the field containing a string instead
		// of an asset. The array check and try/cacth were introduced
		// so that the UI continues to work with the minimally invasive
		// fix before launch on 12/17, but it would be nicer fix the
		// data condition
		try {
			return val && IsArrayProperty(column, Asset) && Array.isArray(val)
				? (val as []).join(', ')
				: val;
		} catch {
			return val;
		}
	}

	public get forUpdate(): boolean {
		return !!this.updateMode;
	}

	public isModified(assetId: string): boolean {
		if (!this.updateMode) {
			return false;
		}

		return !!this.updateService.updateForReview.assetUpdates[assetId];
	}

	public hasRequiredChanges(asset: Asset): boolean {
		return this.updateMode && hasRequiredChanges(asset);
	}

	public get requiredLabel(): string {
		return `*${this.Loc(LocalizedTextIds.RequiredFieldsMissing)}`;
	}
}
